import {
  assignPrototypeAPIs,
  assignTableAPIs,
  type CellData,
  type Column_Internal,
  type columnResizingState,
  type ColumnSizingState,
  type Header,
  type HeaderGroup,
  type Row,
  type RowData,
  type Table_Internal,
  type TableFeature,
  type TableFeatures,
} from '@tanstack/react-table';
import { column_getIsPinned } from '@tanstack/react-table/static-functions';
import type { VirtualItem } from '@tanstack/react-virtual';

import type {
  DataTableColumnGridTemplates,
  TableLayoutFeatureConstructors,
} from './layout-types.js';
import {
  getColumnTemplateEntry,
  getTargetTemplate,
  processBuiltinColumn,
} from './layout-utils.js';
import {
  DATA_TABLE_DEFAULT_COLUMN_WIDTH,
  DATA_TABLE_MIN_WIDTH,
  DATA_TABLE_ROW_BORDER,
  DATA_TABLE_ROW_HEIGHT_MAP,
} from '../../constants.js';
import type { TDataTableFeatures } from '../types/features.types.js';
import type { DataTableVisualVariantTableState } from '../VisualVariants/visual-variants-types.js';

/**
 * Allows to get the gridPosition of a specific header cell in order
 * to place it into the correct place in the template and span it across the correct
 * elements.
 * @internal
 */
export function header_getGridPosition<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData = CellData,
>(
  header: Header<TFeatures, TData, TValue>,
  headers: Array<Header<TFeatures, TData, TValue>>,
) {
  // Get the start of the column index the header should be rendered into,
  // this should be equal to the index of the header cell in the loop passed
  // to this function.
  let gridColumnStart = 0;
  for (const headerInGroup of headers ?? []) {
    if (headerInGroup === header) {
      break;
    }
    gridColumnStart = gridColumnStart + headerInGroup.colSpan;
  }

  // The header already exposes a colSpan value, which can be used to span
  // the header cell element from the start position to the end of the
  // header group.
  const colSpan = header.colSpan;

  // Get the start of the index as this is actually the defined
  // column nesting level. If the column is not nested, it will get a
  // depth of 0, and nesting levels increase going deeper.
  const gridRowStart = header.column.depth;

  // Get the end from the headers depth, as this is where it would be rendered
  // within the tanstack table. As nested columns would be rendered in the
  // lower headerrow. The strato approach here is different, as the header
  // cell should span both header rows
  const gridRowEnd = header.depth;

  // return the gridColumn positioning. As the grid column positioning in
  // css is One-based, it needs an offset of 1 in every entry.
  return {
    gridColumnStart: gridColumnStart + 1,
    gridColumnEnd: `span ${colSpan}`,
    gridRowStart: gridRowStart + 1,
    gridRowEnd: `span ${gridRowEnd - gridRowStart}`,
  };
}

/**
 * Helper function to get the grid column position for a specific column based on its index in the column list. This is used for non-header cells, which are always rendered in the first row of the grid and should span only one column.
 * @internal
 */
export function column_getGridColumnPosition<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData = CellData,
>(column: Column_Internal<TFeatures, TData, TValue>, columnIndex?: number) {
  // Offset the index by one as the CSS grid column is a one based position.
  return { gridColumnStart: (columnIndex ?? 0) + 1, gridColumnEnd: 'span 1' };
}

/**
 * Helper function to get the grid row position for a specific row based on its index in the row list. This is used for all non-header cells, which should be rendered in the body of the table and span all columns.
 * @internal
 */
export function row_getGridRowPosition<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(row: Row<TFeatures, TData>, rowIndex: number = -1) {
  return {
    // Offset the index by one as the CSS grid column is a one based position.
    gridColumnStart: 1,
    // The row should span all columns.
    gridColumnEnd: 'row-end',

    // The CSS grid column is a one based position, so we need to offset the index by one.
    // To account for the virtualized rows at the start of the grid, a spacer row is inserted.
    // Therefore we need to offset the index by two in total
    gridRowStart: rowIndex + 2,
    // The row should span only one line from the start edge.
    gridRowEnd: 'span 1',
  };
}

export function table_getHasFractionalTemplate<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>): boolean {
  const template = table.getGridTemplateColumns();
  return template.includes('fr');
}

export function table_getVisibleHeaderGroupsCount<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>): number {
  if (table.atoms.visualVariantConfig?.get().headers === 'hidden') {
    return 0;
  }

  return table.getHeaderGroups().length;
}

export function table_setObservedHeaderHeight<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>, height: number) {
  if (height !== table.atoms.observedHeaderHeight?.get()) {
    // Required cast to ensure precense of the DataTableFeatures.
    (
      table as unknown as Table_Internal<TDataTableFeatures>
    ).baseAtoms.observedHeaderHeight.set(height);
  }
}

export function table_getHeaderSize<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  table: Table_Internal<TFeatures, TData>,
  headerId: string,
): {
  width: number;
  height: number;
} {
  const escapedId = CSS.escape(headerId);
  const headerSize = table
    .getRowVirtualizationContainer()
    ?.querySelector(
      `[role="columnheader"][data-headerid=${escapedId}], [role="columnheader"][data-columnid=${escapedId}], [role="presentation"][data-columnid=${escapedId}]`,
    )
    ?.getBoundingClientRect();

  if (headerSize === undefined) {
    // Use some standard value if no resize observer is there
    return {
      width: DATA_TABLE_DEFAULT_COLUMN_WIDTH,
      height: table.estimateRowHeight({ isHeaderRow: true }),
    };
  }

  return {
    width: headerSize.width,
    height: headerSize.height,
  };
}

export function table_getColumnOrHeaderWidth<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>, columnOrHeaderId: string): number {
  const hasHiddenHeaders =
    table.atoms.visualVariantConfig?.get().headers === 'hidden';

  // saved width that was stored upon measuring
  const storedWidth = table.getMeasuredColumnWidth(columnOrHeaderId);

  // early exit if all headers are visible and the column is a leaf column
  if (!hasHiddenHeaders && storedWidth !== undefined) {
    return storedWidth;
  }

  const escapedId = CSS.escape(columnOrHeaderId);
  // If all headers are hidden, take the cell in the first rendered row to retrieve the width since all other cells
  // in the same column should have the same width.
  // If the header is not in the virtualizer (because it is a parent column for instance), try to retrieve the height from the rendered element directly.
  const selectorString = hasHiddenHeaders
    ? `[role="cell"][data-columnid="${escapedId}"]`
    : `[role="columnheader"][data-headerid="${escapedId}"], [role="columnheader"][data-columnid="${escapedId}"], [role="presentation"][data-columnid="${escapedId}"]`;

  const columnWidth = table
    .getRowVirtualizationContainer()
    ?.querySelector(selectorString)
    ?.getBoundingClientRect().width;

  return columnWidth ?? DATA_TABLE_DEFAULT_COLUMN_WIDTH;
}

export function table_getTotalHeaderHeight<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  table: Table_Internal<TFeatures, TData>,
  visualVariantOptions: DataTableVisualVariantTableState['visualVariantConfig'],
  visibleLeafColumns: Array<Column_Internal<TFeatures, TData>>,
  tableHeaderGroups: Array<HeaderGroup<TFeatures, TData>>,
  observedHeaderHeight: number,
) {
  // If the headers are hidden, we can opt out earlier.
  if (
    visualVariantOptions?.headers === 'hidden' ||
    visibleLeafColumns.length === 0 ||
    (tableHeaderGroups.length === 1 &&
      tableHeaderGroups.at(0)?.headers.length === 0)
  ) {
    return 0;
  }

  if (observedHeaderHeight > 0) {
    return observedHeaderHeight;
  }

  // if the measured header height is still undefined or 0, estimate it based on row density and amount of header groups,
  // so that the rowVirtualizer can return the most accurate height possible for the first render
  // this prevents an unnecessary vertical scrollbar which would cause wrong column measurements on initial render
  // the header's bottom border is actually 2px, estimateRowHeight only considers 1px so we need to add 1px more
  const bottomBorderOffset = DATA_TABLE_ROW_BORDER;
  const fallbackHeight =
    table.estimateRowHeight({ isHeaderRow: true }) *
      table.getVisibleHeaderGroupsCount() +
    bottomBorderOffset;

  return fallbackHeight;
}

/**
 * Helper function to estimate the row height based on the row density and separation visual variants. This is used for the row virtualizer to estimate the height of rows that are not rendered yet.
 * @internal
 */
export function table_estimateRowHeight<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  table: Table_Internal<TFeatures, TData>,
  index: number | undefined,
  isHeaderRow: boolean | undefined,
  rowDensity: 'default' | 'condensed' | 'comfortable',
  rowSeparation: 'none' | 'horizontalDividers' | 'zebraStripes',
) {
  const rowHeight = DATA_TABLE_ROW_HEIGHT_MAP[rowDensity];
  const paginationFlatRows = table.getPaginatedRowModel().flatRows;
  const isLastRowOnPage = index === paginationFlatRows.length - 1;
  // only consider the border if the index is defined or the estimate is for a header row
  // when estimating rows for an empty table, the index will be undefined and there are no vertical dividers
  const addBorder = index !== undefined || isHeaderRow === true;

  if (addBorder === false || rowSeparation === 'none' || isLastRowOnPage) {
    return rowHeight;
  }

  // add 1px border if rowSeparation is enabled or if this is a header row
  return rowHeight + DATA_TABLE_ROW_BORDER;
}

export function table_getHeaderGridTemplateRows<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>) {
  switch (table.getVisibleHeaderGroupsCount()) {
    case 0:
      return '[body-start]';
    case 1:
      return '[header-start] max-content [header-end body-start]';
    default:
      return '[header-start] max-content max-content [header-end body-start]';
  }
}

export function table_getGridTemplateRows<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  table: Table_Internal<TFeatures, TData>,
  virtualItems: VirtualItem[] | undefined,
): string {
  const measurementsCache = table.getRowVirtualizer()?.measurementsCache;
  const headerRows = table.getHeaderGridTemplateRows();

  if (!measurementsCache || !virtualItems?.length) {
    return headerRows;
  }

  // Determine the template rows for the body, based on the currently rendered rows
  const bodyRows = virtualItems
    .map((virtualItem) => {
      // The virtual item size is the most up to date value as it is measured during rendering,
      // while the measurementsCache can be outdated until the next render.
      // Therefore, the virtual item size is preferred to retrieve the row height.
      const rowHeight =
        virtualItem.size ??
        table.estimateRowHeight({ index: virtualItem.index });
      return `minmax(min-content, ${rowHeight}px)`;
    })
    .join(' ');

  // Add spacer row to account for virtualized rows above the first rendered row
  const firstVirtualItem = virtualItems[0];
  let spacerRowHeight = 0;

  if (firstVirtualItem.index > 0) {
    const scrollPaddingStart = table.getTotalHeaderHeight();
    // Calculate the height by subtracting the scroll padding (header height) from the starting offset of the first visible item.
    spacerRowHeight = firstVirtualItem.start - scrollPaddingStart;
  }

  // combine header, spacer and body rows into a grid row template
  return `${headerRows} ${spacerRowHeight}px ${bodyRows} [body-end]`;
}

export function table_getGridTemplateColumns<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  table: Table_Internal<TFeatures, TData>,
  columnSizing: ColumnSizingState,
  columnResizing: columnResizingState,
) {
  const leftVisibleColumns = table.getLeftVisibleLeafColumns();
  const centerVisibleColumns = table.getCenterVisibleLeafColumns();
  const rightVisibleColumns = table.getRightVisibleLeafColumns();
  if (
    leftVisibleColumns === undefined ||
    centerVisibleColumns === undefined ||
    rightVisibleColumns === undefined ||
    columnSizing === undefined ||
    columnResizing === undefined
  ) {
    return '';
  }

  /**
   * Gets the measured column width if possible.
   * Min/max constraints are already considered during measurement. (see columnVirtualizer measureElement)
   *
   * @param columnId - ID of the column
   * @param isFractionWidth - whether the width is a fraction width
   * @returns measured width in pixels or undefined if not measured yet
   */
  function getMeasuredWidth(
    columnId: string,
    isFractionWidth: boolean = false,
  ): number | undefined {
    // If there are no rows (e.g. loading state), measure the header cell instead
    if (table.getRowModel().rows.length === 0) {
      const headerCell = table
        .getColumnVirtualizer()
        ?.scrollElement?.querySelector(
          `[role="columnheader"][data-columnid="${CSS.escape(columnId)}"]`,
        );
      const headerWidth = headerCell?.getBoundingClientRect().width;

      if (headerWidth === undefined) {
        return DATA_TABLE_MIN_WIDTH;
      }

      // Round up slightly for non-fraction widths to avoid rendering an ellipsis due to subpixel rendering issues
      // don't round for fraction widths to avoid rendering an unnecessary scrollbar for fullWidth tables
      return isFractionWidth ? headerWidth : Math.ceil(headerWidth * 10) / 10;
    }

    return table.getMeasuredColumnWidth(columnId);
  }

  // Grid templates for left, center and right pinned columns
  const columnGridTemplates: DataTableColumnGridTemplates = {
    left: [],
    center: [],
    right: [],
  };

  const orderedVisibleColumns = [
    ...leftVisibleColumns,
    ...centerVisibleColumns,
    ...rightVisibleColumns,
  ];

  // To generate the grid template columns, we need to iterate through all visible columns in order
  for (const column of orderedVisibleColumns) {
    // Handle built-in columns (prefix, suffix, spacer)
    const isBuiltinColumn = processBuiltinColumn(
      column,
      table.atoms.visualVariantConfig?.get().rowDensity,
      table.options.rowThresholds,
      columnGridTemplates,
    );

    if (isBuiltinColumn) {
      continue;
    }

    // Handle width configuration for standard column
    const entry = getColumnTemplateEntry(
      column,
      columnSizing,
      columnResizing,
      getMeasuredWidth,
    );

    if (entry !== undefined) {
      const targetTemplate = getTargetTemplate(
        column_getIsPinned(column),
        columnGridTemplates,
      );

      targetTemplate.push(entry);
    }
  }

  const combinedGridTemplates = [
    ...columnGridTemplates.left,
    ...columnGridTemplates.center,
    ...columnGridTemplates.right,
  ];

  return `${combinedGridTemplates.join(' ')} [row-end]`;
}

/**
 * Constructs the layout feature for the table,
 * including API implementations and prototype assignments.
 * @internal
 */
export function constructTableLayoutFeature(): TableFeature<TableLayoutFeatureConstructors> {
  return {
    getInitialState: (initialState) => {
      return {
        columnMeasureInfo: {},
        observedHeaderHeight: 0,
        columnSizing: {},
        ...initialState,
      };
    },
    assignHeaderPrototype: (prototype, table) => {
      assignPrototypeAPIs('DataTableTableLayoutFeature', prototype, table, {
        header_getGridPosition: {
          fn: (header, headers) => header_getGridPosition(header, headers),
        },
      });
    },
    assignColumnPrototype: (prototype, table) => {
      assignPrototypeAPIs('DataTableTableLayoutFeature', prototype, table, {
        column_getGridColumnPosition: {
          fn: (column, columnIndex) =>
            column_getGridColumnPosition(column, columnIndex),
        },
      });
    },
    assignRowPrototype: (prototype, table) => {
      assignPrototypeAPIs('DataTableTableLayoutFeature', prototype, table, {
        row_getGridRowPosition: {
          fn: (row, rowIndex) => row_getGridRowPosition(row, rowIndex),
        },
      });
    },
    constructTableAPIs: (table) => {
      assignTableAPIs('DataTableTableLayoutFeature', table, {
        table_getHasFractionalTemplate: {
          fn: () => table_getHasFractionalTemplate(table),
        },
        table_getVisibleHeaderGroupsCount: {
          fn: () => table_getVisibleHeaderGroupsCount(table),
        },
        table_setObservedHeaderHeight: {
          fn: (height: number) => table_setObservedHeaderHeight(table, height),
        },
        table_getHeaderSize: {
          fn: (headerId: string) => table_getHeaderSize(table, headerId),
        },
        table_getColumnOrHeaderWidth: {
          fn: (columnOrHeaderId: string) =>
            table_getColumnOrHeaderWidth(table, columnOrHeaderId),
        },
        table_getTotalHeaderHeight: {
          fn: (
            visualVariantOptions,
            visibleLeafColumns,
            tableHeaderGroups,
            observedHeaderHeight,
          ) =>
            table_getTotalHeaderHeight(
              table,
              visualVariantOptions,
              visibleLeafColumns,
              tableHeaderGroups,
              observedHeaderHeight,
            ),
          memoDeps: () => [
            table.atoms.visualVariantConfig?.get(),
            table.getVisibleLeafColumnsWithoutBuiltin(),
            table.getHeaderGroups(),
            table.atoms.observedHeaderHeight?.get(),
          ],
        },
        table_estimateRowHeight: {
          fn: (
            index: number | undefined,
            isHeaderRow: boolean | undefined,
            rowDensity: 'default' | 'condensed' | 'comfortable',
            rowSeparation: 'none' | 'horizontalDividers' | 'zebraStripes',
          ) =>
            table_estimateRowHeight(
              table,
              index,
              isHeaderRow,
              rowDensity,
              rowSeparation,
            ),
          memoDeps: (props) => {
            return [
              props?.index,
              props?.isHeaderRow,
              table.atoms.visualVariantConfig?.get().rowDensity,
              table.atoms.visualVariantConfig?.get().rowSeparation,
            ];
          },
        },
        table_getHeaderGridTemplateRows: {
          fn: () => table_getHeaderGridTemplateRows(table),
        },
        table_getGridTemplateRows: {
          fn: (measurementsCache: VirtualItem[] | undefined) =>
            table_getGridTemplateRows(table, measurementsCache),
          memoDeps: () => {
            const { getVirtualItems, range } = table.getRowVirtualizer() ?? {};

            return [
              getVirtualItems?.(),
              range?.startIndex,
              range?.endIndex,
              table.getVisibleHeaderGroupsCount(),
              table.optionsStore.state.data.length,
            ];
          },
        },
        table_getGridTemplateColumns: {
          fn: (columnSizing, columnSizingInfo) =>
            table_getGridTemplateColumns(table, columnSizing, columnSizingInfo),
          memoDeps: () => {
            return [
              table.atoms.columnSizing?.get(),
              table.atoms.columnResizing?.get(),
              table.atoms.columnPinning?.get(),
              table.atoms.columnOrder?.get(),
              table.atoms.columnVisibility?.get(),
              table.getSubRowIndicatorColumnId(),
              JSON.stringify([...table.getMeasuredColumnWidths()]),
            ];
          },
        },
      });
    },
  };
}

/**
 * Additional feature implementation for table layout.
 * @internal
 */
export const DataTableTableLayoutFeature = constructTableLayoutFeature();
